import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { Badge, Card, CardBody, Col, FormGroup, Input, InputGroup, Label } from 'reactstrap';
import {
  UserSearchFilterContext,
  UserSearchResult,
  UserSearchUser,
} from '../../shared/user/search';
import {
  DriversLicenseResult,
  parseDriversLicenseResult,
  ScannerResult,
  useScannerResult,
} from '../containers/cashier/scanner';
import { SearchUserFilters } from '../containers/housekeeping/attendee/searchUserFilterModal';
import { UserBadges } from '../containers/housekeeping/attendee/utils';
import { useTranslation } from '../translations';
import {
  Fetcher,
  renderName,
  useCheckboxState,
  useFetcher,
  useInputState,
  useObject,
  useQuery,
} from '../utils';
import { useDebounceValue } from '../utils/debounce';
import { DateT } from './Date';
import { PaginationBar } from './PaginationBar';
import { RichMarkdownIt } from './RichMarkdownIt';
import { UserProfilePicture } from './UserProfilePicture';

import './AttendeeSearch.scss';

interface UserSearchProps {
  readonly hideHelp?: boolean;
  readonly redirectUrl?: string;
  readonly redirectOnSingle?: boolean;
  readonly showBornAt?: boolean;
}

export const SearchComponent: FC<UserSearchProps> = ({
  hideHelp,
  redirectUrl,
  redirectOnSingle,
  showBornAt,
}) => {
  const { ts } = useTranslation();
  const history = useHistory();
  const query = useQuery();
  const [autoNavigate, setAutoNavigate] = useCheckboxState(true);
  const [term, setTerm, setTermStr] = useInputState(useQuery().get('term') ?? '');
  const [scannerResult, setScannerResult] = useState<ScannerResult | undefined>();
  const pageSize = 15;
  const [filterContext, setFilterContext] = useState<UserSearchFilterContext>({
    paidUsers: query.get('paidUsers') === 'true',
    printedUsers: query.get('printedUsers') === 'true',
    volunteers: query.get('volunteers') === 'true',
    dealers: query.get('dealers') === 'true',
    badgeName: query.get('badgeName') === 'true',
    page: query.get('page') ? Number.parseInt(query.get('page')!, 10) : 0,
    pageSize,
  });

  const [lastTerm, setLastTerm] = useState(term);
  const [page, setPage] = useObject(filterContext, setFilterContext, 'page');
  const debounceSearch = useDebounceValue({ term, filterContext }, 300);

  useEffect(() => {
    if (term !== lastTerm) {
      setPage(0);
      setLastTerm(term);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, lastTerm]);

  const results = useFetcher<UserSearchResult>(async () => {
    const searchTerm = debounceSearch.term;
    const context = debounceSearch.filterContext;
    if (searchTerm === '') {
      return { total: 0, users: [] };
    }

    const users = await api.searchUsers(searchTerm, context);

    query.set('term', searchTerm);

    if (context.paidUsers) {
      query.set('paidUsers', context.paidUsers.toString());
    } else {
      query.delete('paidUsers');
    }

    if (context.printedUsers) {
      query.set('printedUsers', context.printedUsers.toString());
    } else {
      query.delete('printedUsers');
    }

    if (context.badgeName) {
      query.set('badgeName', context.badgeName.toString());
    } else {
      query.delete('badgeName');
    }

    if (context.volunteers) {
      query.set('volunteers', context.volunteers.toString());
    } else {
      query.delete('volunteers');
    }

    if (context.dealers) {
      query.set('dealers', context.dealers.toString());
    } else {
      query.delete('dealers');
    }

    if (page > 0) {
      query.set('page', page.toString());
    } else {
      query.delete('page');
    }

    history.push({ search: query.toString() });

    return users;
  }, [debounceSearch.term, debounceSearch.filterContext, query, page, history]);

  useScannerResult(
    (res) => {
      if ('uuid' in res) {
        setTermStr(`@CONCAT;UUID${res.uuid!}`);
        setScannerResult(res);
      } else if ('userId' in res) {
        setTermStr(`@CONCAT;UID${res.userId!}`);
        setScannerResult(res);
      } else if ('value' in res) {
        setTermStr(res.value!);
        setScannerResult(res);
      } else {
        const result = res as DriversLicenseResult;
        setTermStr(parseDriversLicenseResult(result));
        setScannerResult(res);
      }
    },
    [setTermStr],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      if (e.key === 'Enter' && Number.isFinite(Number.parseInt(value, 10))) {
        history.push(`/housekeeping/cashier/${e.currentTarget.value}`);
      }
    },
    [history],
  );

  const searchUI = (
    <Input
      autoFocus
      id="searchBox"
      onChange={setTerm}
      onKeyDown={handleKeyDown}
      placeholder={ts('attendee_search.search_users_ellipsis')}
      type="search"
      value={term}
    />
  );

  return (
    <>
      <Col xs={12}>
        <FormGroup>
          {redirectOnSingle ? (
            <div>
              {searchUI}
              <FormGroup check className="border rounded-bottom border-top-0 d-inline-flex p-2 m-0">
                <Input
                  checked={autoNavigate}
                  className="me-1 mb-0 mx-0"
                  id="autoNavigate"
                  name="autoNavigate"
                  onChange={setAutoNavigate}
                  type="checkbox"
                />
                <Label check for="autoNavigate">
                  {ts('attendee_search.enable_auto_navigate')}
                </Label>
              </FormGroup>
            </div>
          ) : (
            <InputGroup>
              {searchUI}
              <SearchUserFilters context={filterContext} setContext={setFilterContext} />
            </InputGroup>
          )}
        </FormGroup>
      </Col>
      <Col xs={12}>
        <Card>
          <CardBody>
            {results.success ? (
              <>
                <SearchResults
                  hideHelp={hideHelp}
                  redirectOnSingle={redirectOnSingle && autoNavigate}
                  redirectUrl={redirectUrl}
                  scannerResult={scannerResult}
                  searchText={debounceSearch.term}
                  showBornAt={showBornAt}
                  users={results.data.users}
                />
                {results.data.total > 0 && (
                  <PaginationBar
                    onChange={setPage}
                    page={page}
                    totalPages={Math.ceil(results.data.total / pageSize)}
                  />
                )}
              </>
            ) : (
              <Fetcher inline result={results} />
            )}
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

interface SearchResultsProps {
  readonly hideHelp?: boolean;
  readonly users: UserSearchUser[];
  readonly redirectUrl?: string;
  readonly redirectOnSingle?: boolean;
  readonly showBornAt?: boolean;
  readonly searchText: string;
  readonly scannerResult?: ScannerResult;
}

const SearchResults: FC<SearchResultsProps> = ({
  hideHelp,
  users,
  redirectUrl,
  redirectOnSingle,
  searchText,
  showBornAt,
  scannerResult,
}) => {
  const { ts } = useTranslation();
  function getURL(id: number): string {
    let url = `${redirectUrl ?? '/housekeeping/attendees/user'}/${id}`;

    if (searchText.startsWith('@ANSI')) {
      url += `?barcode=${searchText}`;
    }

    return url;
  }

  if (users.length === 0) {
    return (
      <SearchHelp hideHelp={hideHelp} noResults={searchText !== ''} scannerResult={scannerResult} />
    );
  }

  if (users.length === 1 && redirectOnSingle) {
    const user = users[0]!;

    if (
      searchText.startsWith('@CONCAT;UUID') ||
      searchText.startsWith('@CONCAT;UID') ||
      (user.firstName &&
        user.lastName &&
        searchText.toLowerCase().includes(user.firstName.toLowerCase()) &&
        searchText.toLowerCase().includes(user.lastName.toLowerCase()))
    ) {
      return (
        <>
          <Redirect to={getURL(users[0]!.id)} />
          <h3>{ts('attendee_search.one_result_found')}</h3>
          <p>{ts('redirecting_ellipsis')}</p>
        </>
      );
    }
  }

  return users.map((user) => (
    <div className="search-item" id={`viewUser${user.id}`} key={user.id}>
      <Link to={getURL(user.id)}>
        <Col className="userListItem clearfix-after" xs={12}>
          <UserProfilePicture url={user.profilePictureUrl} />
          <div>
            <HighlightedText highlight={searchText} text={renderName(user)} />
            &nbsp;
            <UserBadges showBornAt={showBornAt} user={user} />{' '}
            <SearchBadges searchText={searchText} user={user} />
          </div>
          <div>{user.registration?.badgeName}</div>
        </Col>
      </Link>
      <hr />
    </div>
  ));
};

interface SearchBadgesProps {
  readonly user: UserSearchUser;
  readonly searchText: string;
}

const SearchBadges: FC<SearchBadgesProps> = ({ user, searchText }) => {
  const { ts } = useTranslation();
  const badges: React.JSX.Element[] = [];

  if (user.email?.includes(searchText)) {
    badges.push(
      <Badge color="info" key="email">
        {ts('common.email')}: <HighlightedText highlight={searchText} text={user.email} />
      </Badge>,
    );
  }

  if (user.registration?.badgeName.toLowerCase().includes(searchText.toLowerCase())) {
    badges.push(
      <Badge color="info" key="email">
        {ts('common.badge_name')}:{' '}
        <HighlightedText highlight={searchText} text={user.registration.badgeName} />
      </Badge>,
    );
  }

  if (user.id.toString() === searchText) {
    badges.push(
      <Badge color="info" key="userid">
        {ts('id')}: #{user.id}
      </Badge>,
    );
  }

  if (user.registration?.badgeId.toString() === searchText) {
    badges.push(
      <Badge color="info" key="badgeId">
        {ts('common.badge_id')}: #{user.registration.badgeId}
      </Badge>,
    );
  }

  if (user.bornAt) {
    const dateOnly = user.bornAt.toISOString().split('T')[0]!;
    if (searchText.includes(dateOnly)) {
      badges.push(
        <Badge color="info" key="dob">
          {ts('common.dob')}: <DateT value={user.bornAt} />
        </Badge>,
      );
    }
  }

  if (badges.length === 0) {
    return null;
  }

  return <span className="badge-list compact matchedBy">{badges}</span>;
};

function escapeRegExp(string: string): string {
  return string.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`);
}

interface HighlightTextProps {
  readonly text: string;
  readonly highlight: string;
}

export const HighlightedText: FC<HighlightTextProps> = ({ text, highlight }) => {
  const split = highlight.split(' ').map((t) => escapeRegExp(t));
  const parts = text.split(new RegExp(`(${split.join('|')})`, 'gi'));

  return parts.map((part, index) => {
    const matches = split.some((t) => part.toLowerCase() === t.toLowerCase());
    return (
      <span
        className={matches ? 'highlight' : ''}
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        style={{ fontWeight: matches ? 'bold' : undefined }}
      >
        {part}
      </span>
    );
  });
};

interface SearchHelpProps {
  readonly hideHelp?: boolean;
  readonly noResults: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  readonly scannerResult?: ScannerResult;
}

const SearchHelp: FC<SearchHelpProps> = ({ hideHelp, noResults }) => {
  const { ts } = useTranslation();
  if (hideHelp) {
    return <NoResults noResults={noResults} />;
  }

  return (
    <>
      <h3>{ts('attendee_search.search_parameters')}</h3>
      {noResults && <p>{ts('attendee_search.no_results_found')}</p>}
      <p>
        <RichMarkdownIt source={ts('attendee_search.to_search_for')} />
      </p>
      <ul>
        <li>{ts('common.badge_id')}</li>
        <li>{ts('common.username')}</li>
        <li>{ts('common.to_search_for__item__name')}</li>
        <li>{ts('common.phone_number')}</li>
        <li>{ts('common.email_address')}</li>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        <li>{ts('common.date_of_birth')} (YYYY-MM-DD)</li>
      </ul>
    </>
  );
};

const NoResults: FC<{ readonly noResults: boolean }> = ({ noResults }) => {
  const { ts } = useTranslation();
  return (
    <div className="search-empty-state">
      <h4>
        {ts(
          noResults ? 'attendee_search.no_results_found' : 'attendee_search.scan_barcode_or_type',
        )}
      </h4>
    </div>
  );
};
